import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Navbar from 'react-bootstrap/lib/Navbar';
import Nav from 'react-bootstrap/lib/Nav';
import NavItem from 'react-bootstrap/lib/NavItem';
import { LinkContainer } from 'react-router-bootstrap';
import BMenuItem from 'react-bootstrap/lib/MenuItem';
import NavDropdown from 'react-bootstrap/lib/NavDropdown';
import Badge from 'react-bootstrap/lib/Badge';
import Fade from 'react-bootstrap/lib/Fade';
import Thumbnail from 'components/Thumbnails/Thumbnail';
import SvgIcon from 'components/SvgIcon/SvgIcon';
import { useHistory } from 'react-router-dom';
// import useAuth from 'hooks/useAuth';
import { trackEvent } from 'helpers/Analytics';
import { shouldShowCelloReferralWidget } from 'helpers/celloReferral';
import {
  getBuyerLoginPath,
  getSellerLoginPath,
} from 'helpers/clientOnboarding';
import styles from './MainHeader.module.scss';
import NavBarCTA from '../Main/NavBarCTA/NavBarCTA';
import { Gift } from 'lucide-react';
import ApplicationCreditsSection from './ApplicationCreditsSection/ApplicationCreditsSection';
import {
  LOG_IN_HREF,
  LOG_IN_LABEL,
  SIGN_UP_HREF,
  SIGN_UP_LABEL,
} from '../authLinks';

const CELLO_CUSTOM_LAUNCHER_CLASS = 'twine-cello-referral-launcher';
const CELLO_CUSTOM_LAUNCHER_ID = 'twine-cello-referral-launcher';
const CELLO_BUTTON_TEXT = 'Earn $50';

const brandMark = (href) => (
  <Navbar.Brand>
    <a
      href={href}
      aria-label="Twine home"
      style={{ display: 'flex', alignItems: 'center' }}
    >
      <div className={styles.brand}>
        <SvgIcon icon="logo" alt="Twine" />
      </div>
      <span
        style={{
          fontSize: '10px',
          marginLeft: '3px',
          marginTop: '-1px',
          color: '#666',
          fontWeight: 400,
          fontFamily: 'Arial, Helvetica, "sans-serif"',
          lineHeight: 1,
          letterSpacing: '0.02em',
          userSelect: 'none',
        }}
        aria-label="Registered Trademark"
      >
        &reg;
      </span>
    </a>
  </Navbar.Brand>
);

const MainHeader = ({
  user,
  count,
  loadedCount,
  loadedOldCount,
  oldCount,
  unreadMessages,
  isProject,
  logoOnly = false,
}) => {
  // const { user } = useAuth(); // Potentially causing an issue as user auth already checked in App.js
  const {
    location: { pathname },
  } = useHistory();
  const [navExpanded, setNavExpanded] = useState(false);
  const displaySignUpButton = false;
  const [displayApplyButton, setDisplayApplyButton] = useState(false);
  const [displayPostJobButton, setDisplayPostJobButton] = useState(false);
  const [displayUpsellButton, setDisplayUpsellButton] = useState(false);

  const showCelloReferralUi =
    !!process.env.CELLO_PRODUCT_ID &&
    !!user &&
    shouldShowCelloReferralWidget(user);

  const onToggleNav = () => setNavExpanded((prev) => !prev);

  const ariNewBadge = (
    <Badge
      style={{
        backgroundColor: '#0777DA',
        fontSize: '10px',
        marginLeft: '2px',
        marginBottom: '2px',
        verticalAlign: 'middle',
      }}
    >
      New
    </Badge>
  );

  const handleHeaderCTAToDisplay = (user, isProject) => {
    setDisplayApplyButton(false);
    setDisplayPostJobButton(false);
    setDisplayUpsellButton(false);

    if (user) {
      if (user?.type === 'seller') {
        // When signed in as a freelancer and viewing the /projects page, the CTA button in the header should be "Apply" which will scroll the user down to the box at the bottom of the page. Use an animated scroll.
        if (isProject) {
          setDisplayApplyButton(true);
        } else {
          setDisplayUpsellButton(true);
        }
      } else if (user?.type === 'buyer') {
        // When signed in as a client and on the /projects page, show the current CTA "Post a Job".
        setDisplayPostJobButton(true);
      }
    }
  };

  useEffect(() => {
    handleHeaderCTAToDisplay(user, isProject);
  }, [user, isProject]);

  const authMobile = user && (
    <div className="visible-xs-block">
      <Nav key="navbar-1" navbar>
        {user.type === 'seller' && (
          <>
            <NavItem href="/jobs" key="jobs-nav">
              Find Jobs
            </NavItem>
            <NavItem
              href="/ari"
              key="ari-nav"
              onClick={() => trackEvent('ari-nav-clicked')}
            >
              Ari {ariNewBadge}
            </NavItem>
            <NavItem href="/saved-jobs" key="saved-jobs-nav">
              Saved Jobs
            </NavItem>
          </>
        )}
        {user.type === 'buyer' && (
          <LinkContainer to="/howitworks" key="howitworks-link">
            <NavItem key="howitworks-nav" onClick={onToggleNav}>
              How it works
            </NavItem>
          </LinkContainer>
        )}
        <NavItem key="messages-link" href="/messages">
          Messages{' '}
          <Fade
            in={
              user && typeof unreadMessages === 'number' && unreadMessages > 0
            }
          >
            {user &&
            typeof unreadMessages === 'number' &&
            unreadMessages > 0 ? (
              <Badge
                style={{
                  position: 'absolute',
                  right: '10px',
                  backgroundColor: '#0777DA',
                }}
              >
                {unreadMessages}
              </Badge>
            ) : (
              <span />
            )}
          </Fade>
        </NavItem>
        {user.type === 'buyer' && (
          <NavItem key="browse-nav" href="/browse">
            Browse for Experts
          </NavItem>
        )}
      </Nav>

      {user.type === 'seller' && (
        <Nav
          key="navbar-2"
          navbar
          style={{
            borderTop: '1px solid #e5e7eb',
            boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1)',
          }}
        >
          <NavItem key="managepitches-nav" href="/manage/pitches">
            Applications
          </NavItem>
          <NavItem key="earings-nav" href="/earnings">
            Earnings
          </NavItem>
          <LinkContainer to={user.links.main_relative} key="portfolio-link">
            <NavItem onClick={onToggleNav} key="portfolio-nav">
              Portfolio
            </NavItem>
          </LinkContainer>
          <NavItem key="manageprojects-nav" href="/projects/manage">
            Portfolio Projects
          </NavItem>
        </Nav>
      )}
      {user.type === 'buyer' && (
        <Nav
          key="navbar-2"
          navbar
          style={{
            borderTop: '1px solid #e5e7eb',
            boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1)',
          }}
        >
          <NavItem key="managejobs-navitem" href="/ats">
            Posted Projects
          </NavItem>
          <NavItem key="payments-navitem" href="/payments">
            Payments
          </NavItem>
          <NavItem key="shortlist-navitem" href="/shortlist">
            Talent Pool
          </NavItem>
        </Nav>
      )}

      <Nav
        key="navbar-3"
        navbar
        style={{
          borderTop: '1px solid #e5e7eb',
          boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1)',
        }}
      >
        <NavItem key="notifcations-navitem" href="/notifications">
          Notifications{' '}
          <Fade
            in={user && loadedCount && loadedOldCount && count + oldCount > 0}
          >
            {user && loadedCount && loadedOldCount && count + oldCount > 0 ? (
              <Badge
                style={{
                  position: 'absolute',
                  right: '15px',
                  backgroundColor: '#0777DA',
                }}
              >
                {count + oldCount}
              </Badge>
            ) : (
              <span />
            )}
          </Fade>
        </NavItem>
        {showCelloReferralUi ? (
          <NavItem
            key="cello-referral-mobile"
            className={`${CELLO_CUSTOM_LAUNCHER_CLASS} ${styles.celloMenuLauncher}`}
            href="#"
            style={{ position: 'relative' }}
            onClick={(e) => {
              e.preventDefault();
              trackEvent('cello-button-click', {
                location: 'header',
              });
              setTimeout(() => {
                onToggleNav();
              }, 0);
            }}
          >
            {CELLO_BUTTON_TEXT}
          </NavItem>
        ) : null}
        <NavItem key="settings-navitem" href="/settings">
          Settings
        </NavItem>
        <NavItem key="signout-navitem" href="/signout" onClick={onToggleNav}>
          <SvgIcon icon="logout" size="14" style={{ marginRight: '8px' }} />
          Log out
        </NavItem>
        {typeof user.admin_user_id !== 'undefined' ? (
          <NavItem href="/adminSwitchBack.php" onClick={onToggleNav}>
            Admin Switch Back
          </NavItem>
        ) : null}
      </Nav>
      {user.type === 'seller' && <ApplicationCreditsSection mobile />}
    </div>
  );
  const noAuthMobile = !user && (
    <div key="no-user-div" className="visible-xs-block">
      <Nav key="navbar-4" navbar>
        <NavItem href="/jobs" key="jobs-nav">
          Find work
        </NavItem>
        <NavItem
          href="/freelance/ari"
          key="ari-nav"
          onClick={() => trackEvent('ari-nav-clicked')}
        >
          Ari {ariNewBadge}
        </NavItem>
        {/* Plain link, not a dropdown. It previously had no href and sent
            clicks to /freelancers; desktop already links to /howitworks. */}
        <NavItem href="/howitworks" key="howitworks-nav" onClick={onToggleNav}>
          How it works
        </NavItem>
      </Nav>

      <Nav
        key="navbar-5"
        navbar
        style={{
          borderTop: '1px solid #e5e7eb',
          boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1)',
        }}
      >
        <NavItem href="https://help.twine.net" onClick={onToggleNav}>
          Help Center
        </NavItem>
        <NavItem key="signup-nav" href={SIGN_UP_HREF}>
          {SIGN_UP_LABEL}
        </NavItem>
        <NavItem key="signin-nav" href={LOG_IN_HREF}>
          {LOG_IN_LABEL}
        </NavItem>
      </Nav>
    </div>
  );

  const messages = (
    <li key="messages-link">
      <a href="/messages" key="messages-nav" title="Messages">
        Messages{' '}
        <Fade
          in={user && typeof unreadMessages === 'number' && unreadMessages > 0}
        >
          {user && typeof unreadMessages === 'number' && unreadMessages > 0 ? (
            <Badge
              style={{
                position: 'absolute',
                right: 0,
                top: '14px',
                backgroundColor: '#0777DA',
              }}
            >
              {unreadMessages}
            </Badge>
          ) : (
            <span />
          )}
        </Fade>
      </a>
    </li>
  );

  const isCtaBrowse = useMemo(
    () => /(hire-a-freelancer|messages|howitworks|whyusetwine)/.test(pathname),
    [pathname]
  );

  const isCtaPricing = useMemo(
    () =>
      /(manage|settings|projects|notifications|shortlist)/.test(pathname) ||
      (user && pathname.match(user.links.main_relative)),
    [user, pathname]
  );

  const headerCTA = (isCtaBrowse || isCtaPricing) && (
    <Navbar.Form key="header-CTA-nav" pullLeft className="hidden-xs hidden-sm">
      <LinkContainer
        to={isCtaBrowse ? '/browse' : isCtaPricing ? '/pricing' : ''}
      >
        <NavBarCTA
          displayUpsellButton={displayUpsellButton}
          displaySignUpButton={displaySignUpButton}
          displayApplyButton={displayApplyButton}
          displayPostJobButton={displayPostJobButton}
        />
      </LinkContainer>
    </Navbar.Form>
  );

  const upgradeToProCTA = (
    <Navbar.Form
      key="header-CTA-nav"
      pullLeft
      className="hidden-xs hidden-sm lato-bold"
    >
      <LinkContainer to="/pricing/creative">
        <NavBarCTA
          displayUpsellButton={displayUpsellButton}
          displaySignUpButton={displaySignUpButton}
          displayApplyButton={displayApplyButton}
          displayPostJobButton={displayPostJobButton}
        />
      </LinkContainer>
    </Navbar.Form>
  );

  const notifications = (
    <LinkContainer to="/notifications">
      <NavItem
        key="notification-nav"
        className="hidden-sm"
        title="Recent notifications"
      >
        <SvgIcon size="18" icon="bell-o" />
        <Fade
          in={user && loadedCount && loadedOldCount && count + oldCount > 0}
        >
          {user && loadedCount && loadedOldCount && count + oldCount > 0 ? (
            <Badge
              style={{
                position: 'absolute',
                left: '25px',
                top: '10px',
                backgroundColor: '#0777DA',
              }}
            >
              {count + oldCount}
            </Badge>
          ) : (
            <span />
          )}
        </Fade>
      </NavItem>
    </LinkContainer>
  );

  const celloHeaderButton = showCelloReferralUi ? (
    <Navbar.Form
      key="cello-referral-header"
      pullLeft
      className={`hidden-sm ${styles.celloHeaderForm}`}
    >
      <a
        href="#"
        title="Refer a friend"
        aria-label="Refer a friend"
        onClick={(e) => {
          e.preventDefault();
          trackEvent('cello-button-click', {
            location: 'header',
          });
        }}
        className={styles.celloHeaderAnchor}
        id={CELLO_CUSTOM_LAUNCHER_ID}
      >
        <span
          className={`${styles.celloHeaderLauncher} ${styles.celloHeaderOutline} ${CELLO_CUSTOM_LAUNCHER_CLASS}`}
        >
          <Gift
            size={18}
            color="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          />
          <span className={styles.celloHeaderLabel}>{CELLO_BUTTON_TEXT}</span>
        </span>
      </a>
    </Navbar.Form>
  ) : null;

  const celloReferralMenuItem = showCelloReferralUi ? (
    <BMenuItem
      key="cello-referral-launcher"
      className={`${CELLO_CUSTOM_LAUNCHER_CLASS}`}
      style={{ position: 'relative' }}
      href="#"
      onClick={(e) => {
        e.preventDefault();
        trackEvent('cello-button-click', {
          location: 'menu',
        });
      }}
    >
      <Gift
        size={14}
        color="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ marginRight: '8px' }}
        aria-hidden
      />
      {CELLO_BUTTON_TEXT}
    </BMenuItem>
  ) : null;

  const sharedDropdowns = (
    <>
      <BMenuItem key="payments-item" href="/payments">
        <SvgIcon icon="credit-card" size="14" style={{ marginRight: '8px' }} />
        Payments to Experts
      </BMenuItem>
      <BMenuItem key="shortlist-item" href="/shortlist">
        <SvgIcon
          icon="heart-outline"
          size="14"
          style={{ marginRight: '8px' }}
        />
        Talent Pool
      </BMenuItem>
      <BMenuItem key="shortlist-divider" divider />
      <BMenuItem key="notifications-item" href="/notifications">
        <SvgIcon icon="bell-o" size="14" style={{ marginRight: '8px' }} />
        Notifications
      </BMenuItem>
      <BMenuItem key="settings-item" href="/settings">
        <SvgIcon icon="cog" size="14" style={{ marginRight: '8px' }} />
        Settings
      </BMenuItem>
      <BMenuItem key="signout-item" href="/signout">
        <SvgIcon icon="logout" size="14" style={{ marginRight: '8px' }} />
        Log out
      </BMenuItem>
      {user && typeof user.admin_user_id !== 'undefined' && (
        <BMenuItem
          href="/adminSwitchBack.php"
          key="admin-switch-back-2"
          onClick={onToggleNav}
        >
          <SvgIcon icon="user" size="14" style={{ marginRight: '8px' }} />
          Admin Switch Back
        </BMenuItem>
      )}
    </>
  );

  const navSections = {
    left: {
      noAuth: (
        <>
          <NavDropdown
            key="navdropdown-1"
            title="Hire an Expert"
            className="hidden-xs"
          >
            <BMenuItem
              key="browse-all"
              href="/hire-a-freelancer"
              onClick={onToggleNav}
            >
              Browse All Experts
            </BMenuItem>
            <BMenuItem key="business-divider" divider />
            <div className="dropdown-header">Business</div>
            <BMenuItem
              key="business-startups"
              href="https://www.twine.net/hire-a-freelancer/startups"
              onClick={onToggleNav}
            >
              Startups
            </BMenuItem>
            <BMenuItem
              key="business-brands"
              href="https://www.twine.net/hire-a-freelancer/consumer-brands"
              onClick={onToggleNav}
            >
              Brands and D2C
            </BMenuItem>
            <BMenuItem
              key="business-enterprise"
              href="/enterprise"
              onClick={onToggleNav}
            >
              Enterprise
            </BMenuItem>
            <BMenuItem key="creative-divider" divider />
            <div className="dropdown-header">Creative</div>
            <BMenuItem
              key="creative-video"
              href="/hire-a-freelancer/video-and-animation"
              onClick={onToggleNav}
            >
              Video & Animation
            </BMenuItem>
            <BMenuItem
              key="creative-design"
              href="/hire-a-freelancer/graphic-design"
              onClick={onToggleNav}
            >
              Graphics & Design
            </BMenuItem>
            <BMenuItem
              key="creative-music"
              href="/hire-a-freelancer/music"
              onClick={onToggleNav}
            >
              Music & Audio
            </BMenuItem>
            <BMenuItem
              key="creative-marketing"
              href="/hire-a-freelancer/digital-marketing"
              onClick={onToggleNav}
            >
              Marketing
            </BMenuItem>
            <BMenuItem key="engineering-divider" divider />
            <div className="dropdown-header">Engineering</div>
            <BMenuItem
              key="engineering-dev"
              href="/hire-a-freelancer/programming"
              onClick={onToggleNav}
            >
              App & Web Development
            </BMenuItem>
            <BMenuItem key="engineering-ai" href="/ai" onClick={onToggleNav}>
              AI and Machine Learning
            </BMenuItem>
          </NavDropdown>
          <LinkContainer to="/jobs">
            <NavItem key="jobs_nav_2">Find Work</NavItem>
          </LinkContainer>
          <NavItem
            key="ari_nav"
            href="/freelance/ari"
            onClick={() => trackEvent('ari-nav-clicked')}
          >
            Ari {ariNewBadge}
          </NavItem>
          <NavItem
            key="howitworks_nav_2"
            href="/howitworks"
            onClick={onToggleNav}
          >
            How it Works
          </NavItem>
          <NavItem key="blog_nav" href="/blog">
            Blog
          </NavItem>
          <NavDropdown key="navdropdown-2" title="More" className="hidden-xs">
            <BMenuItem
              key="one-page-website-link"
              href="/freelance/one-page-website"
              onClick={onToggleNav}
            >
              <SvgIcon icon="sphere" size="14" style={{ marginRight: '8px' }} />
              One-page Website
            </BMenuItem>
            <BMenuItem
              key="application-writer-link"
              href="/freelance/cover-letter-proposal-writing-tool"
              onClick={onToggleNav}
            >
              <SvgIcon icon="pencil" size="14" style={{ marginRight: '8px' }} />
              Cover Letter Writer
            </BMenuItem>
            <BMenuItem
              key="toolkit-link"
              href="/toolkits/freelancer"
              onClick={onToggleNav}
            >
              <SvgIcon icon="wrench" size="14" style={{ marginRight: '8px' }} />
              Freelancer Toolkit
            </BMenuItem>
          </NavDropdown>
        </>
      ),
      client: user && (
        <>
          <LinkContainer to="/ats">
            <NavItem key="managejobs-nav">Posted Projects</NavItem>
          </LinkContainer>
          {messages}
          <LinkContainer key="shortlist-nav" to="/shortlist">
            <NavItem>Talent Pool</NavItem>
          </LinkContainer>
          <LinkContainer
            key="browse-nav"
            to="/browse"
            onClick={() => trackEvent('header-click-browse')}
          >
            <NavItem>Browse</NavItem>
          </LinkContainer>
        </>
      ),
      freelancer: user && (
        <>
          <LinkContainer to="/jobs">
            <NavItem key="jobs_nav_2" title="Find Jobs">
              Find Jobs
            </NavItem>
          </LinkContainer>
          <LinkContainer
            to="/ari"
            onClick={() => trackEvent('ari-nav-clicked')}
          >
            <NavItem key="ari_nav" title="Ari">
              Ari {ariNewBadge}
            </NavItem>
          </LinkContainer>
          <LinkContainer to="/saved-jobs">
            <NavItem key="saved_jobs_nav" title="Saved Jobs">
              Saved Jobs
            </NavItem>
          </LinkContainer>
          <LinkContainer to="/manage/pitches">
            <NavItem key="applications_nav" title="Applications">
              Applications
            </NavItem>
          </LinkContainer>
          {messages}
          <NavDropdown
            key="navdropdown-1"
            title="Tools"
            id="tools-dropdown"
            className="hidden-xs"
          >
            <BMenuItem
              key="one-page-website-link"
              href="/freelance/one-page-website"
              onClick={onToggleNav}
            >
              <SvgIcon icon="sphere" size="14" style={{ marginRight: '8px' }} />
              One-page Website
            </BMenuItem>
            <BMenuItem
              key="application-writer-link"
              href="/freelance/cover-letter-proposal-writing-tool"
              onClick={onToggleNav}
            >
              <SvgIcon icon="pencil" size="14" style={{ marginRight: '8px' }} />
              Cover Letter Writer
            </BMenuItem>
            <BMenuItem
              key="betterpic-link"
              href="/tools/betterpic"
              onClick={onToggleNav}
            >
              <SvgIcon icon="camera" size="14" style={{ marginRight: '8px' }} />
              AI Headshots
            </BMenuItem>
            <BMenuItem
              key="toolkit-link"
              href="/toolkits/freelancer"
              onClick={onToggleNav}
            >
              <SvgIcon icon="wrench" size="14" style={{ marginRight: '8px' }} />
              Freelancer Toolkit
            </BMenuItem>
            <BMenuItem key="blog-item" href="/blog" onClick={onToggleNav}>
              <SvgIcon
                icon="newspaper"
                size="14"
                style={{ marginRight: '8px' }}
              />
              News and blog
            </BMenuItem>
          </NavDropdown>
        </>
      ),
    },
    right: {
      noAuth: (
        <>
          {headerCTA}
          <NavItem key="signin-nav" href={LOG_IN_HREF}>
            {LOG_IN_LABEL}
          </NavItem>
          <NavItem key="signup-nav" href={SIGN_UP_HREF}>
            {SIGN_UP_LABEL}
          </NavItem>
        </>
      ),
      client: {
        items: (
          <>
            {/* Client Side CTA */}
            {headerCTA}
            {celloHeaderButton}
            {notifications}
          </>
        ),
        dropdown: user && (
          <>
            <BMenuItem key="notifications-pricing" href="/pricing">
              <SvgIcon
                icon="pricing"
                size="14"
                style={{ marginRight: '8px' }}
              />
              Pricing
            </BMenuItem>
            <BMenuItem key="pricing-divider" divider />
            <BMenuItem key="notifications-item" href="/notifications">
              <SvgIcon icon="bell-o" size="14" style={{ marginRight: '8px' }} />
              Notifications
            </BMenuItem>
            <BMenuItem key="payments-item" href="/payments">
              <SvgIcon
                icon="credit-card"
                size="14"
                style={{ marginRight: '8px' }}
              />
              Payments
            </BMenuItem>
            {celloReferralMenuItem}
            <BMenuItem key="settings-item" href="/settings">
              <SvgIcon icon="cog" size="14" style={{ marginRight: '8px' }} />
              Settings
            </BMenuItem>
            <BMenuItem key="signout-item" href="/signout" onClick={onToggleNav}>
              <SvgIcon icon="logout" size="14" style={{ marginRight: '8px' }} />
              Log out
            </BMenuItem>
          </>
        ),
      },
      freelancer: {
        items: (
          <>
            {/* Displays for a freelancer that isn't a a pro user  */}

            {!user?.pro && upgradeToProCTA}
            {celloHeaderButton}
            {notifications}
          </>
        ),
        dropdown: user && (
          <>
            {!user.pro && (
              <>
                <BMenuItem key="upgrade-1" href="/pricing/creative">
                  <SvgIcon
                    icon="star"
                    size="14"
                    style={{ marginRight: '8px' }}
                  />
                  <strong>Upgrade to Pro</strong>
                </BMenuItem>
                <BMenuItem key="upgrade-2" divider />
              </>
            )}
            <BMenuItem key="portfolio-item" href={user.links.main_relative}>
              <SvgIcon
                icon="profile"
                size="14"
                style={{ marginRight: '8px' }}
              />
              Portfolio
            </BMenuItem>
            <BMenuItem key="manageprojects-1" href="/projects/manage">
              <SvgIcon icon="image" size="14" style={{ marginRight: '8px' }} />
              Portfolio Projects
            </BMenuItem>
            <BMenuItem key="managepitches-1" href="/manage/pitches">
              <SvgIcon icon="paper" size="14" style={{ marginRight: '8px' }} />
              Applications
            </BMenuItem>
            <BMenuItem key="earnings-1" href="/earnings">
              <SvgIcon icon="chart" size="14" style={{ marginRight: '8px' }} />
              Earnings
            </BMenuItem>
            {celloReferralMenuItem}
            <BMenuItem key="manageprojects-2" divider />
            <BMenuItem key="managejobs-item" href="/ats">
              <SvgIcon
                icon="briefcase"
                size="14"
                style={{ marginRight: '8px' }}
              />
              {user.type === 'buyer' ? 'Posted Projects' : 'Posted Jobs'}
            </BMenuItem>
            {sharedDropdowns}
            <li
              key="credits-section"
              role="presentation"
              style={{ padding: 0, margin: 0 }}
            >
              <ApplicationCreditsSection />
            </li>
          </>
        ),
      },
    },
  };

  const isPost = /post/.test(pathname);

  const desktop = (
    <div>
      <Nav key="navbar-6" navbar className="hidden-xs">
        {isPost
          ? null
          : !user
          ? navSections.left.noAuth
          : user.type === 'seller'
          ? navSections.left.freelancer
          : navSections.left.client}
      </Nav>

      <Nav key="navbar-7" navbar pullRight className="hidden-xs">
        {isPost
          ? null
          : !user
          ? navSections.right.noAuth
          : user.type === 'seller'
          ? navSections.right.freelancer.items
          : navSections.right.client.items}
        {user && (
          <NavDropdown
            key="navdropdown-2"
            noCaret
            title={
              <div
                style={{ display: '-webkit-inline-box' }}
                title="Account options"
                aria-label="Account options"
              >
                <Thumbnail
                  key="thumbnail"
                  thumbnails={user.avatars}
                  circle
                  style={{ width: '40px', height: '40px' }}
                />
                <span
                  className="caret"
                  style={{ marginTop: '20px', marginLeft: '5px' }}
                />
              </div>
            }
            id="image-dropdown"
            className={`hidden-xs ${styles.navMenu}`}
          >
            {user.type === 'seller'
              ? navSections.right.freelancer.dropdown
              : navSections.right.client.dropdown}
          </NavDropdown>
        )}
      </Nav>
    </div>
  );

  // Determine logo link based on user type
  let logoHref = '/';
  if (user && user.type === 'seller') {
    logoHref = getSellerLoginPath();
  } else if (user && user.type === 'buyer') {
    logoHref = getBuyerLoginPath();
  }

  // Auth pages: full header chrome with logo only (links home).
  if (logoOnly) {
    return (
      <Navbar staticTop>
        <Navbar.Header>{brandMark('/')}</Navbar.Header>
      </Navbar>
    );
  }

  return (
    <Navbar staticTop onToggle={onToggleNav} expanded={navExpanded}>
      <Navbar.Header>
        {brandMark(logoHref)}
        <Navbar.Toggle />
        {(!user || user.type === 'buyer' || !user.pro) && (
          <div
            className="visible-xs-block right"
            style={{ marginTop: '20px', marginRight: '10px' }}
          >
            <NavBarCTA
              displayUpsellButton={displayUpsellButton}
              displaySignUpButton={displaySignUpButton}
              displayApplyButton={displayApplyButton}
              displayPostJobButton={displayPostJobButton}
            />
          </div>
        )}
      </Navbar.Header>

      <Navbar.Collapse>
        {authMobile}

        {noAuthMobile}

        {desktop}
      </Navbar.Collapse>
    </Navbar>
  );
};

MainHeader.propTypes = {
  user: PropTypes.object,
  count: PropTypes.number,
  loadedCount: PropTypes.bool,
  loadedOldCount: PropTypes.bool,
  oldCount: PropTypes.number,
  unreadMessages: PropTypes.number,
  isProject: PropTypes.bool,
  logoOnly: PropTypes.bool,
};

export default MainHeader;
